/** @jsxRuntime automatic */
/** @jsxImportSource react */
import type { NodoEnLinea } from "@gestiondoc/shared";
import type { ReactNode } from "react";
import type { ResaltadoDeHoja } from "./HojaISO.js";

/**
 * El texto corrido de la hoja: los tramos con sus marcas (negrita, cursiva, subrayado), las
 * referencias a otros documentos y, encima, los resaltados de las marcas del revisor.
 */

/** Las marcas que caen en un bloque dado. */
export function resaltadosDe(
  resaltados: ResaltadoDeHoja[] | undefined,
  bloqueId: string,
): ResaltadoDeHoja[] {
  return resaltados?.filter((r) => r.bloqueId === bloqueId) ?? [];
}

/**
 * Texto con sus marcas y referencias. Las marcas se anidan en un orden fijo: b > i > u.
 *
 * Fase 4: los tramos marcados por el revisor van en un `<mark>` (uno por pedazo de texto que
 * toca); el número de la marca se dibuja con CSS (`::before`), así no entra en el texto del
 * párrafo y los desplazamientos que mide la pantalla al seleccionar siguen siendo los de `textoDe`.
 */
export function enLinea(
  nodos: NodoEnLinea[] | undefined,
  resaltados: ResaltadoDeHoja[] = [],
): ReactNode {
  if (!nodos || nodos.length === 0) return null;
  let desde = 0;
  return nodos.map((n, i) => {
    if (n.type === "referencia") {
      const inicio = desde;
      desde += n.attrs.codigo.length;
      const ref = (
        <span className="hoja-referencia" data-bloque={n.attrs.id}>
          {n.attrs.codigo}
        </span>
      );
      const r = resaltados.find((x) => x.inicio < desde && x.fin > inicio);
      return r ? (
        <MarcaDeRevision key={n.attrs.id} resaltado={r} primero={r.inicio >= inicio}>
          {ref}
        </MarcaDeRevision>
      ) : (
        <span key={n.attrs.id}>{ref}</span>
      );
    }
    const formato = new Set((n.marks ?? []).map((m) => m.type));
    const conFormato = (texto: string): ReactNode => {
      let nodo: ReactNode = texto;
      if (formato.has("underline")) nodo = <u>{nodo}</u>;
      if (formato.has("italic")) nodo = <em>{nodo}</em>;
      if (formato.has("bold")) nodo = <strong>{nodo}</strong>;
      return nodo;
    };
    const inicio = desde;
    desde += n.text.length;
    const tocan = resaltados.filter((x) => x.inicio < desde && x.fin > inicio);
    if (!tocan.length) {
      // Los tramos de texto no tienen id: la posición dentro del párrafo es su identidad.
      // biome-ignore lint/suspicious/noArrayIndexKey: son hojas sin identidad propia y nunca se reordenan
      return <span key={i}>{conFormato(n.text)}</span>;
    }
    // Se parte el tramo donde empieza o termina alguna marca.
    const cortes = new Set([0, n.text.length]);
    for (const x of tocan) {
      cortes.add(Math.max(0, x.inicio - inicio));
      cortes.add(Math.min(n.text.length, x.fin - inicio));
    }
    const puntos = [...cortes].sort((p, q) => p - q);
    return (
      // biome-ignore lint/suspicious/noArrayIndexKey: ídem
      <span key={i}>
        {puntos.slice(0, -1).map((a, k) => {
          const b = puntos[k + 1] as number;
          const pedazo = n.text.slice(a, b);
          const r = tocan
            .filter((x) => x.inicio <= inicio + a && x.fin >= inicio + b)
            .sort((p, q) => p.numero - q.numero)[0];
          return r ? (
            <MarcaDeRevision key={a} resaltado={r} primero={r.inicio === inicio + a}>
              {conFormato(pedazo)}
            </MarcaDeRevision>
          ) : (
            <span key={a}>{conFormato(pedazo)}</span>
          );
        })}
      </span>
    );
  });
}

function MarcaDeRevision({
  resaltado: r,
  primero,
  children,
}: {
  resaltado: ResaltadoDeHoja;
  primero: boolean;
  children: ReactNode;
}) {
  return (
    <mark
      className="hoja-marca"
      data-tipo={r.tipo}
      data-correccion={r.numero}
      // Solo el primer pedazo lleva el número a la vista.
      data-numero={primero && !r.sinNumero ? r.numero : undefined}
      data-activa={r.activo ? "" : undefined}
      title={r.titulo}
    >
      {children}
    </mark>
  );
}
